import { FunctionPlugin, HyperFormula, FunctionArgumentType, SimpleRangeValue, CellError } from "hyperformula";
import { isFormula } from "./utils/util";
import { rangeLabelToIndex } from "./components/process-design/cell/Utils";
const hf = HyperFormula.buildFromSheets([], { licenseKey: "gpl-v3" });
function initializeHyperFomrulaInstance(values) {
  const sheetName = "TempSheet";
  hf.addSheet(sheetName);
  const sheetId = hf.getSheetId(sheetName);

  // Populate the temporary sheet with values for the SUM calculation
  values.forEach((value, index) => {
    hf.setCellContents({ sheet: sheetId, row: index, col: 0 }, [[value]]);
  });

  // Define the range based on array length, e.g., A1:A3 for 3 values
  const range = `A1:A${values.length}`;

  return { range, sheetId };
}
const getIfsFunctionEnums = {
  1: "count",
  2: "counta",
  3: "sum",
  4: "average",
  5: "max",
  6: "min",
  7: "first",
  8: "last",
  9: "most",
  10: "least",
};
const aggregateFunctionsEnum = {
  1: { name: "AVERAGE", isRef2: false },
  2: { name: "COUNT", isRef2: false },
  3: { name: "COUNTA", isRef2: false },
  4: { name: "MAX", isRef2: false },
  5: { name: "MIN", isRef2: false },
  6: { name: "PRODUCT", isRef2: false },
  7: { name: "STDEV.S", isRef2: false },
  8: { name: "STDEV.P", isRef2: false },
  9: { name: "SUM", isRef2: false },
  10: { name: "VAR.S", isRef2: false },
  11: { name: "VAR.P", isRef2: false },
  12: { name: "MEDIAN", isRef2: false },
  13: { name: "MODE.SNGL", isRef2: false },
  14: { name: "LARGE", isRef2: true },
  15: { name: "SMALL", isRef2: true },
};
const customFunctions = {
  count: (parameters) => {
    return parameters.reduce((total, num) => total + (isNaN(num) ? 0 : 1), 0);
  },
  counta: (parameters) => {
    return parameters.reduce((total, num) => total + (num === "" ? 0 : 1), 0);
  },
  sum: createCustomFunction((range) => `=SUM(${range})`),
  average: createCustomFunction((range) => `=AVERAGE(${range})`),
  max: (parameters) => {
    return Math.max(...parameters);
  },
  min: (parameters) => {
    return Math.min(...parameters);
  },
  first: (parameters) => {
    return parameters[0];
  },
  last: (parameters) => {
    return parameters[parameters.length - 1];
  },
  most: (parameters) => {
    const frequency = {};
    parameters.forEach((element) => {
      frequency[element] = (frequency[element] || 0) + 1;
    });
    return Object.keys(frequency).reduce((a, b) => (frequency[a] >= frequency[b] ? a : b));
  },
  least: (parameters) => {
    const frequency = {};
    parameters.forEach((element) => {
      frequency[element] = (frequency[element] || 0) + 1;
    });

    return Object.keys(frequency).reduce((a, b) => (frequency[a] <= frequency[b] ? a : b));
  },
  product: createCustomFunction((range) => `=PRODUCT(${range})`),
  "stdev.s": createCustomFunction((range) => `=STDEV.S(${range})`),
  "stdev.p": createCustomFunction((range) => `=STDEV.P(${range})`),
  "var.s": createCustomFunction((range) => `=VAR.S(${range})`),
  "var.p": createCustomFunction((range) => `=VAR.P(${range})`),
  median: createCustomFunction((range) => `=MEDIAN(${range})`),
  "mode.sngl": createCustomFunction((range) => `=MODE.SNGL(${range})`),
  large: createCustomFunction((range, ref2) => `=LARGE(${range},${ref2})`),
  small: createCustomFunction((range, ref2) => `=SMALL(${range},${ref2})`),
};
function createCustomFunction(formulaBuilder) {
  return (parameters, ref2) => {
    const { range, sheetId } = initializeHyperFomrulaInstance(parameters);
    const formula = formulaBuilder(range, ref2);
    const result = hf.calculateFormula(formula, sheetId);
    hf.removeSheet(sheetId);

    return result;
  };
}
const getifsBehaviorFunctions = {
  0: () => true,
  1: (item) => item != 0,
  2: (item) => !isNaN(item),
  3: (item) => isNaN(item),
  4: (item) => item?.constructor?.name !== "CellError",
  5: (item, context, rowIndex) => {
    const { rootGetters } = context;
    return rootGetters["cells/getRowVisibilityByIndex"](rowIndex);
  },
};
const aggregateBehaviorFunctions = {
  0: (item) => {
    if (!isFormula(item.rawValue)) return item;
    const rawValue = item.rawValue?.toLowerCase();
    return !rawValue?.includes("aggregate") && !rawValue?.includes("subtotal");
  },
  1: (item, context) => {
    const { rootGetters } = context;
    const isRowVisible = rootGetters["cells/getRowVisibilityByIndex"](item.rowIndex);
    return isRowVisible && aggregateBehaviorFunctions[0](item);
  },
  2: (item) => item.type !== "ERROR" && aggregateBehaviorFunctions[0](item),
  3: (item, context) => item.type !== "ERROR" && aggregateBehaviorFunctions[1](item, context),
  4: () => true,
  5: (item, context) => {
    const { rootGetters } = context;
    return rootGetters["cells/getRowVisibilityByIndex"](item.rowIndex);
  },
  6: (item) => item.type !== "ERROR",
  7: (item, context) => aggregateBehaviorFunctions[5](item, context) && aggregateBehaviorFunctions[6](item),
};
const escapeRegExp = (s) => {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const evaluateCriteria = (value, criteria) => {
  // Check for wildcards
  if (String(criteria).includes("*") || String(criteria).includes("?")) {
    const pattern = new RegExp("^" + criteria.split("*").map(escapeRegExp).join(".*").replace(/\?/g, ".") + "$");
    return pattern.test(value);
  }

  // Check for comparison operators
  const operators = /(<>)|([<>]=?|=)/.exec(criteria);
  if (operators) {
    const operator = operators[0];
    const numericValue = parseFloat(value);
    const numericCriteria = parseFloat(criteria.substr(operator.length));
    switch (operator) {
      case ">":
        return numericValue > numericCriteria;
      case ">=":
        return numericValue >= numericCriteria;
      case "<":
        return numericValue < numericCriteria;
      case "<=":
        return numericValue <= numericCriteria;
      case "=":
        return numericValue == numericCriteria;
      case "<>":
        return numericValue != numericCriteria;
      default:
        return false;
    }
  }

  // Default case: strict equality
  return value == criteria;
};

const customFilter = (parameters) => {
  const dataRange = parameters[0];
  return dataRange.filter((item, index) => {
    for (let j = 1; j < parameters.length; j += 2) {
      if (!evaluateCriteria(parameters[j][index], parameters[j + 1])) {
        return false;
      }
    }
    return true;
  });
};

function extractRanges(context, args, formulaAddress) {
  const { rootState } = context;
  const { sheet, row, col } = formulaAddress;
  return args.map((x) => {
    if (x.type === "CELL_REFERENCE") {
      return {
        start: { sheet, col: x.reference.col + col, row: x.reference.row + row },
        end: { sheet, col: x.reference.col + col, row: x.reference.row + row },
      };
    } else if (x.type === "NAMED_EXPRESSION") {
      const expression = rootState.table.namedExpressions
        .find((expression) => expression.name === x.expressionName)
        .expression.replace(/\$/g, "");

      const [startCol, startRow, endCol, endRow] = rangeLabelToIndex(expression);
      return {
        start: { sheet, col: startCol, row: startRow },
        end: { sheet, col: endCol, row: endRow },
      };
    } else {
      return {
        start: { sheet, col: x.start.col + col, row: x.start.row + row },
        end: { sheet, col: x.end.col + col, row: x.end.row + row },
      };
    }
  });
}

function isEmptyValue(value) {
  return typeof value === "symbol" && value.toString() === "Symbol(Empty value)";
}
// https://github.com/handsontable/hyperformula/issues/1265
function removeEmptySymbols(data) {
  return data.map((x) => (x?.data ? x.data.flat().map((item) => (isEmptyValue(item) ? "" : item)) : x));
}
function getRefinedArgs(rawArgs, args, state) {
  const result = [];
  rawArgs.forEach((arg, index) => {
    if (arg.type == "CELL_REFERENCE") {
      const relativeAddr = arg.reference;
      const absoluteAddr = relativeAddr.toSimpleCellAddress(state.formulaAddress);
      const cellFormula = this.serialization.getCellSerialized(absoluteAddr);
      const cellValue = this.serialization.getCellValue(absoluteAddr);
      result.push({ value: cellValue, rawValue: cellFormula, rowIndex: absoluteAddr.row });
    } else if (arg.type == "CELL_RANGE") {
      const startAdd = arg.start.toSimpleCellAddress(state.formulaAddress);
      const endAdd = arg.end.toSimpleCellAddress(state.formulaAddress);
      const minX = Math.min(startAdd.col, endAdd.col);
      const maxX = Math.max(startAdd.col, endAdd.col);
      const minY = Math.min(startAdd.row, endAdd.row);
      const maxY = Math.max(startAdd.row, endAdd.row);
      const rowLength = maxY - minY + 1;
      const matrix = new Array(rowLength).fill(null).map((item) => []);
      for (let i = minY; i <= maxY; i++) {
        for (let j = minX; j <= maxX; j++) {
          const cellFormula = this.serialization.getCellSerialized({ sheet: startAdd.sheet, col: j, row: i });
          const cellValue = this.serialization.getCellValue({ sheet: startAdd.sheet, col: j, row: i });
          let type = null;
          if (args[index] && args[index].data && args[index].data[i - minY] && args[index].data[i - minY][j - minX]) {
            type = args[index].data[i - minY][j - minX].constructor?.name === "CellError" ? "ERROR" : null;
          }
          matrix[i - minY][j - minX] = { value: cellValue, rawValue: cellFormula, type, rowIndex: i };
        }
      }
      result.push(matrix);
    } else if (arg.type == "NAMED_EXPRESSION") {
      const context = this.config.context;
      const expression = context.rootState.table.namedExpressions.find((x) => x.name === arg.expressionName).expression;
      const sheet = state.formulaAddress.sheet;
      const [startColumn, startRow, endColumn, endRow] = rangeLabelToIndex(expression);

      const rowLength = endRow - startRow + 1;
      const matrix = new Array(rowLength).fill(null).map((item) => []);
      for (let row = startRow; row <= endRow; row++) {
        for (let col = startColumn; col <= endColumn; col++) {
          const cellFormula = this.serialization.getCellSerialized({ sheet, col, row });

          const cellValue = this.serialization.getCellValue({ sheet, col, row });
          const type =
            args[index].data[row - startRow][col - startColumn].constructor.name === "CellError" ? "ERROR" : null;
          matrix[row - startRow][col - startColumn] = { value: cellValue, rawValue: cellFormula, type, rowIndex: row };
        }
      }
      result.push(matrix);
    } else {
      result.push({ value: arg.value, rawValue: arg.value });
    }
  });
  return result;
}
export class MyCustomPlugin extends FunctionPlugin {
  getifs(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GET_IFS"), (...x) => {
      x = removeEmptySymbols(x);
      const dataRange = x[0];
      const functionCode = x[1];
      const behavior = x[2];
      const filteredArgs = state.formulaVertex.formula.args[0];
      for (let i = 3; i < x.length; i += 2) {
        if (x[i].length !== dataRange.length)
          return new CellError("VALUE", "Data Range and All Criteria Range must be same size");
      }

      const ranges = extractRanges(this.config.context, [filteredArgs], state.formulaAddress);
      const filteredData = customFilter([dataRange, ...x.slice(3)])
        .filter((item, index) => {
          const rowIndex = ranges[0].start.row + index;
          return getifsBehaviorFunctions[behavior](item, this.config.context, rowIndex);
        })
        .map((x) => x.type ?? x);
      return customFunctions[getIfsFunctionEnums[functionCode]](filteredData);
    });
  }
  aggregate(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("AGGREGATE"), (...args) => {
      const refinedArgs = getRefinedArgs.call(this, ast.args, args, state);
      const [functionCode, behavior, { data: calculatedData }] = args;
      const errorBehaviors = [2, 3, 6, 7];
      const filteredArgs = state.formulaVertex.formula.args.slice(2);
      const functionObj = aggregateFunctionsEnum[functionCode];
      try {
        // Return error if calculated data contains invalid types and behavior isn't in errorBehaviors
        if (
          calculatedData.some((item) => item[0].constructor.name === "CellError") &&
          !errorBehaviors.includes(behavior)
        ) {
          const errorItem = calculatedData.find((x) => x[0].type);
          return new CellError(
            `${errorItem[0].type}`,
            `There is an error in your data range. Problematic entry: ${errorItem[0].message}`
          );
        }

        // Arguments should only be of type range, no value neither function.
        if (
          !functionObj.isRef2 &&
          filteredArgs.some(
            (x) => x.type !== "CELL_RANGE" && x.type !== "CELL_REFERENCE" && x.type !== "NAMED_EXPRESSION"
          )
        )
          return new CellError("VALUE", "Incorrect formula");

        // type2 functions should only have one additional parameter.
        if (functionObj.isRef2 && filteredArgs.length > 2)
          return new CellError("ERROR", "The LARGE and SMALL functions should only have one ref parameters");

        const ref2 = functionObj.isRef2 && refinedArgs[3].value;
        const dataRange = functionObj.isRef2 ? refinedArgs[2].flat(2) : refinedArgs.slice(2).flat(2);
        // Filter and process data based on behavior functions
        const filteredData = dataRange
          .filter((item) => aggregateBehaviorFunctions[behavior](item, this.config.context))
          .map((x) => x.value);
        const result = customFunctions[functionObj.name.toLowerCase()](filteredData, ref2);

        if (result.type) return new CellError(`${result.type}`, `${result.message}`);
        return result;
      } catch (error) {
        return new CellError("ERROR", error);
      }
    });
  }
}

// Static property with the custom functions definitions
MyCustomPlugin.implementedFunctions = {
  GET_IFS: {
    method: "getifs",
    parameters: [
      { argumentType: FunctionArgumentType.RANGE },
      { argumentType: FunctionArgumentType.INTEGER, minValue: 1, maxValue: 10 },
      { argumentType: FunctionArgumentType.INTEGER, minValue: 0, maxValue: 5 },
      { argumentType: FunctionArgumentType.RANGE },
      { argumentType: FunctionArgumentType.STRING },
    ],
    repeatLastArgs: 2,
  },
  AGGREGATE: {
    method: "aggregate",
    parameters: [
      { argumentType: FunctionArgumentType.INTEGER, minValue: 1, maxValue: 15 },
      { argumentType: FunctionArgumentType.INTEGER, minValue: 0, maxValue: 7 },
      { argumentType: FunctionArgumentType.RANGE },
    ],
    repeatLastArgs: 1,
  },
};

// Custom functions translations
export const MyCustomPluginTranslations = {
  enGB: {
    GET_IFS: "GETIFS",
    AGGREGATE: "AGGREGATE",
  },
  enUS: {
    GET_IFS: "GETIFS",
    AGGREGATE: "AGGREGATE",
  },
};
